/* eslint-disable @typescript-eslint/no-empty-interface */
import {
  Grid,
  Stack,
  Typography,
  Paper,
  ImageList,
  ImageListItem,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
} from '@mui/material'
import { MoreVert, Close } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getEvent, getSite, removeEvent } from '../../model'
import { EventViewModel, SiteViewModel } from '../ViewModel'
import NewEventDialog from './NewEventDialog'
import { useAuth } from '../Auth'
import { useAppNavigate } from '../../Paths'

function EventInfoPage() {
  const [event, setEvent] = useState<EventViewModel>(null)
  const [site, setSite] = useState<SiteViewModel>(null)

  const { siteId, eventId } = useParams()
  const { isAdmin } = useAuth()
  const appNavigate = useAppNavigate()

  async function initialize() {
    const eventData = await getEvent(siteId, eventId)
    setEvent(eventData)
    console.log(JSON.stringify(eventData))

    const siteData = getSite(siteId)
    setSite(siteData)
  }

  useEffect(() => {
    let unsubcribed = false
    if (unsubcribed) return
    console.log(`Event Info Params: ${siteId} - ${eventId}`)
    initialize()

    return () => {
      unsubcribed = true
      setEvent(null)
    }
  }, [])

  const [sitePopoverAnchor, setSitePopoverAnchor] =
    React.useState<HTMLElement | null>(null)

  const handleSitePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSitePopoverAnchor(event.currentTarget)
  }

  const handleSitePopoverClose = () => {
    setSitePopoverAnchor(null)
  }

  const sitePopoverOpen = Boolean(sitePopoverAnchor)

  return (
    <Stack padding={2}>
      <Stack>
        <Stack direction={'row'} justifyContent="space-between">
          <Typography
            variant="h4"
            color={'black'}
            aria-owns={sitePopoverOpen ? 'mouse-over-popover' : undefined}
            aria-haspopup="true"
            onMouseEnter={handleSitePopoverOpen}
            onMouseLeave={handleSitePopoverClose}
          >
            {site?.siteName}
          </Typography>
          {isAdmin && (
            <MoreMenu
              handleEventDelete={async () => {
                try {
                  await removeEvent(siteId, eventId)
                  appNavigate.toSite(siteId)
                } catch (e) {
                  console.log('Deleting event fail: ' + e)
                }
              }}
            />
          )}
        </Stack>

        <Typography variant="h6">{site?.id}</Typography>
        <Typography variant="h6">
          {event?.creationDate} {event?.timeOfRecord}
        </Typography>
        <Popover
          id="mouse-over-popover"
          sx={{
            pointerEvents: 'none',
          }}
          elevation={1}
          open={sitePopoverOpen}
          anchorEl={sitePopoverAnchor}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          onClose={handleSitePopoverClose}
          disableRestoreFocus
        >
          <Stack p={2}>
            <Detail label="Site Type" text={site?.siteType} />
            <Detail label="Region" text={site?.region} />
            <Detail label="Catchement" text={site?.catchment} />
            <Detail label="Zone" text={site?.zone} />
            <Detail label="Water way" text={site?.waterway} />
            <Detail label="Best Gauge Owner" text={site?.bestGaugeOwner} />
            <Detail label="Gauge Station" text={site?.gaugeStation} />
          </Stack>
        </Popover>
      </Stack>
      <Grid container>
        {/* Left Frame */}
        <Grid item xs={12} sm={8} md={8} lg={9}>
          <Stack spacing={1}>
            <Detail
              label="Water level (Gauge height)"
              text={event?.waterLevel}
            />
            <Detail label="Flow" text={event?.flowObservation} />
            <Detail label="Water Level" text={event?.waterlevelObservation} />
            <Detail label="Water Flow Rate" text={event?.flowRate} />
            <Detail label="Site Note" text={event?.siteNotes} />
            <Typography fontWeight={'bold'}>Upstream Images</Typography>
            <ImageList
              sx={{ width: 'fit-content', height: 'fit-content' }}
              cols={2}
              rowHeight={300}
            >
              {event?.upstreamImageRefs?.map((item) => (
                <ImageListItem key={item}>
                  <img
                    src={`${item}`}
                    srcSet={`${item}`}
                    alt={item}
                    loading="lazy"
                  />
                </ImageListItem>
              ))}
            </ImageList>
            <Typography fontWeight={'bold'}>Downstream Images</Typography>
            <ImageList
              sx={{ width: 'fit-content', height: 'fit-content' }}
              cols={2}
              rowHeight={300}
            >
              {event?.downstreamImageRefs?.map((item) => (
                <ImageListItem key={item}>
                  <img
                    src={`${item}`}
                    srcSet={`${item}`}
                    alt={item}
                    loading="lazy"
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Stack>
        </Grid>
        {/* Right Frame */}
        <Grid item xs>
          <Stack spacing={2} paddingX={2}>
            <WeatherSectionCard event={event} />
            <WaterQualitySectionCard event={event} />
            <RainfallPriorSectionCard event={event} />
            <FloodEventSectionCard event={event} />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  )
}

function MoreMenu({
  handleEventDelete,
}: {
  handleEventDelete: () => Promise<void>
}) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const { siteId, eventId } = useParams()

  const [eventModalOpen, setEventModalOpen] = useState(false)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleDeleteClose = () => {
    setDeleteOpen(false)
  }

  return (
    <div>
      <IconButton size="large" onClick={handleClick}>
        <MoreVert fontSize="inherit" />
      </IconButton>
      <Menu
        elevation={1}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setEventModalOpen(true)
            setAnchorEl(null)
          }}
        >
          Edit
        </MenuItem>
        {/* <MenuItem onClick={handleClose}>Print</MenuItem> */}
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            setDeleteOpen(true)
          }}
        >
          Delete
        </MenuItem>
      </Menu>
      <NewEventDialog
        siteId={siteId}
        eventId={eventId}
        open={eventModalOpen}
        onClose={() => {
          setEventModalOpen(false)
        }}
      />
      <Dialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle>Deleting this event from {siteId}</DialogTitle>
        <DialogContent>
          <Typography></Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            disabled={deleteLoading}
            variant="contained"
            onClick={handleDeleteClose}
          >
            Cancel
          </Button>
          <Button
            color={'error'}
            disabled={deleteLoading}
            variant="contained"
            onClick={async () => {
              await handleEventDelete()
              handleDeleteClose()
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

interface DetailProps {
  label: string
  text: string | number
  measure?: string
}

function Detail({ label, text, measure }: DetailProps) {
  return (
    <Typography fontWeight="bold">
      {label}:
      <Typography component={'span'}>
        {' '}
        {text && text !== '' ? `${text} ${measure ?? ''}` : '-'}
      </Typography>
    </Typography>
  )
}

function WeatherSectionCard({ event }: { event?: EventViewModel }) {
  return (
    <Stack>
      <Typography>Weather</Typography>
      <Paper elevation={1} sx={{ padding: 2 }}>
        <Detail label="Current rainfall" text={event?.rainfallObservation} />
        <Detail label="Wind" text={event?.windObservation} />
        <Detail label="Sky" text={event?.skyObservation} />
        <Detail label="Air Temperature" text={event?.airTemp} measure="˚C" />
      </Paper>
    </Stack>
  )
}

function WaterQualitySectionCard({ event }: { event?: EventViewModel }) {
  return (
    <Stack>
      <Typography>Water Quality</Typography>
      <Paper elevation={1} sx={{ padding: 2 }}>
        <Detail
          label="Depth of sample"
          text={event?.sampleDepth}
          measure="cm"
        />
        <Detail label="Temperature" text={event?.waterTemp} measure="˚C" />
        <Detail label="pH" text={event?.pH} />
        <Detail label="EC" text={event?.EC} measure="µs/cm" />
        <Detail label="Turbidity" text={event?.turbidity} measure="NTU's" />
      </Paper>
    </Stack>
  )
}

function RainfallPriorSectionCard({ event }: { event?: EventViewModel }) {
  return (
    <Stack>
      <Typography>Rainfall prior to day of sampling</Typography>
      <Paper elevation={1} sx={{ padding: 2 }}>
        <Detail
          label="Past 24h"
          text={event?.pastRainfall?.past24}
          measure="mm"
        />
        <Detail
          label="Past week"
          text={event?.pastRainfall?.pastWeek}
          measure="mm"
        />
        <Detail
          label="Past month"
          text={event?.pastRainfall?.pastMonth}
          measure="mm"
        />
      </Paper>
    </Stack>
  )
}

function FloodEventSectionCard({ event }: { event?: EventViewModel }) {
  return (
    <Stack>
      <Typography>Flood Event Monitoring</Typography>
      <Paper elevation={1} sx={{ padding: 2 }}>
        <Detail
          label="Lab sample taken"
          text={event?.labSample ? 'Yes' : 'No'}
        />
        <Detail label="A Bottle" text={event?.aBottle ? 'Yes' : 'No'} />
        <Detail label="D Bottle" text={event?.dBottle ? 'Yes' : 'No'} />
        <Detail label="Flood comment" text={event?.floodComments} />
      </Paper>
    </Stack>
  )
}

export default EventInfoPage
